import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";
import { useAuth } from "../AuthContext";

const SPORTS = ["Football", "Basketball", "Baseball", "Hockey", "Soccer",
  "Tennis", "Golf", "Track and Field", "Boxing", "MMA"];

const CHIP = {
  queued: { bg: "#e5e7eb", fg: "#374151", label: "Queued" },
  building: { bg: "#dbeafe", fg: "#1d4ed8", label: "Building…" },
  needs_input: { bg: "#fef3c7", fg: "#b45309", label: "Needs your input" },
  live: { bg: "#dcfce7", fg: "#15803d", label: "Live" },
  failed: { bg: "#fee2e2", fg: "#b91c1c", label: "Failed" },
};

// The cockpit shell (`.dsl`) sets a near-white --text for its dark theme; native
// inputs/selects/textareas keep a white background regardless, so without an
// explicit dark color here text is near-invisible (matches AthleteSites.jsx's
// INPUT_STYLE, which does the same for the same reason).
const FIELD = {
  width: "100%", padding: "8px 10px", borderRadius: 8,
  border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
  background: "#fff", color: "#111",
};

export default function SiteJobs({ onSiteLive }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [quota, setQuota] = useState(null);       // {limit, used, remaining, resets_at}
  const [limitDraft, setLimitDraft] = useState("");
  const [health, setHealth] = useState(null);   // {ok, detail, at, stale}
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ athlete_name: "", sport: "", theme: false,
    color_hint: "", notes: "" });
  const [formErr, setFormErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});     // job id -> free-text draft
  const [busy, setBusy] = useState("");            // job id mid-action
  const liveSeen = useRef(new Set());

  const load = useCallback(() => api.get("/site-jobs").then(({ data }) => {
    const rows = Array.isArray(data) ? data : [];
    rows.forEach((j) => {
      if (j.status === "live" && !liveSeen.current.has(j.id)) {
        liveSeen.current.add(j.id);
        if (liveSeen.current.initialized) onSiteLive?.();
      }
    });
    liveSeen.current.initialized = true;
    setJobs(rows);
  }).catch(() => {}), [onSiteLive]);

  const loadQuota = useCallback(() => api.get("/site-jobs/quota")
    .then(({ data }) => setQuota(data)).catch(() => {}), []);

  const loadHealth = useCallback(() => api.get("/site-jobs/health")
    .then(({ data }) => setHealth(data)).catch(() => {}), []);

  useEffect(() => {
    load(); loadQuota(); loadHealth();
    const t = setInterval(() => { load(); loadQuota(); loadHealth(); }, 30000);
    return () => clearInterval(t);
  }, [load]);

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.athlete_name.trim()) { setFormErr("Athlete name is required."); return; }
    setSubmitting(true); setFormErr("");
    try {
      await api.post("/site-jobs", {
        athlete_name: form.athlete_name.trim(),
        sport: form.sport || null,
        theme: form.theme ? "light" : null,
        color_hint: form.color_hint.trim() || null,
        notes: form.notes.trim() || null,
      });
      await loadQuota();
      setShowForm(false);
      setForm({ athlete_name: "", sport: "", theme: false, color_hint: "", notes: "" });
      await load();
    } catch (err) {
      setFormErr(err?.response?.data?.detail || "Request failed.");
    } finally { setSubmitting(false); }
  }

  async function sendAnswer(job, text) {
    const answer = (text || "").trim();
    if (!answer) return;
    setBusy(job.id);
    try {
      await api.post(`/site-jobs/${job.id}/answer`, { answer });
      setAnswers((a) => ({ ...a, [job.id]: "" }));
      await load();
    } catch { /* strip refreshes on next poll */ } finally { setBusy(""); }
  }

  async function rerun(job) {
    setBusy(job.id);
    try { await api.post(`/site-jobs/${job.id}/rerun`); await load(); await loadQuota(); }
    catch { } finally { setBusy(""); }
  }

  async function saveLimit() {
    const n = parseInt(limitDraft, 10);
    if (Number.isNaN(n) || n < 0) return;
    try {
      const { data } = await api.post("/site-jobs/quota", { limit: n });
      setQuota(data); setLimitDraft("");
    } catch { /* meter refreshes on next poll */ }
  }

  async function dismiss(job) {
    setBusy(job.id);
    try { await api.delete(`/site-jobs/${job.id}`); await load(); }
    catch { } finally { setBusy(""); }
  }

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Site requests</h3>
        <button onClick={() => setShowForm((v) => !v)}
          style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #d1d5db",
                   background: "#111827", color: "#fff", cursor: "pointer", fontSize: 13 }}>
          {showForm ? "Cancel" : "Request new site"}
        </button>

        {health && (() => {
          const down = !health.ok || health.stale;
          const label = health.stale ? "Builder not checked in"
            : health.ok ? "Builder online" : "Builder offline";
          return (
            <span title={health.stale
                    ? `Last check-in: ${health.at || "never"} — the Mac may be asleep or the agent stopped.`
                    : (health.detail || "")}
              style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5,
                       color: down ? "#b91c1c" : "#15803d" }}>
              <span style={{ width: 7, height: 7, borderRadius: 999,
                             background: down ? "#b91c1c" : "#16a34a" }} />
              {label}
              {down && !health.stale && health.detail
                ? ` — ${health.detail.slice(0, 80)}` : ""}
            </span>
          );
        })()}

        {quota && (
          <span style={{ fontSize: 12, color: quota.remaining === 0 ? "#b45309" : "#6b7280",
                         display: "flex", alignItems: "center", gap: 6 }}>
            <strong style={{ fontWeight: 600 }}>{quota.used} of {quota.limit}</strong>
            builds used this week
            {quota.remaining === 0 && " — queued requests start Monday"}
            {isAdmin && (
              <>
                <input value={limitDraft} onChange={(e) => setLimitDraft(e.target.value)}
                  placeholder={String(quota.limit)} inputMode="numeric"
                  title="Weekly build limit"
                  style={{ width: 46, padding: "2px 6px", borderRadius: 6, fontSize: 12,
                           border: "1px solid #d1d5db", background: "#fff", color: "#111",
                           marginLeft: 6 }} />
                <button onClick={saveLimit} disabled={!limitDraft.trim()}
                  style={{ padding: "2px 8px", borderRadius: 6, fontSize: 12,
                           border: "1px solid #d1d5db", cursor: "pointer" }}>
                  Set limit
                </button>
              </>
            )}
          </span>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ marginTop: 12, padding: 16, borderRadius: 12,
            border: "1px solid #e5e7eb", display: "grid", gap: 10, maxWidth: 520 }}>
          <input style={FIELD} placeholder="Athlete name (required)"
            value={form.athlete_name}
            onChange={(e) => setForm({ ...form, athlete_name: e.target.value })} />
          <div style={{ display: "flex", gap: 10 }}>
            <select style={{ ...FIELD, width: "50%" }} value={form.sport}
              onChange={(e) => setForm({ ...form, sport: e.target.value })}>
              <option value="">Sport (optional)</option>
              {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={form.theme}
                onChange={(e) => setForm({ ...form, theme: e.target.checked })} />
              Light theme
            </label>
          </div>
          <input style={FIELD} placeholder="Color hint, e.g. 'Raiders silver/black' (optional)"
            value={form.color_hint}
            onChange={(e) => setForm({ ...form, color_hint: e.target.value })} />
          <textarea style={{ ...FIELD, minHeight: 70 }} value={form.notes}
            placeholder="Notes for the builder — career focus, era, anything that steers the site (optional)"
            onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div style={{ fontSize: 12, color: "#6b7280" }}>
            Only the name is required — everything else just helps the build.
          </div>
          {formErr && <div style={{ color: "#b91c1c", fontSize: 13 }}>{formErr}</div>}
          <button disabled={submitting} type="submit"
            style={{ padding: "8px 14px", borderRadius: 8, border: "none",
                     background: "#111827", color: "#fff", cursor: "pointer" }}>
            {submitting ? "Submitting…" : "Submit request"}
          </button>
        </form>
      )}

      {jobs.length > 0 && (
        <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
          {jobs.map((j) => {
            const chip = CHIP[j.status] || CHIP.queued;
            return (
              <div key={j.id} style={{ padding: "10px 14px", borderRadius: 10,
                  border: "1px solid #e5e7eb", display: "grid", gap: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: 14 }}>{j.athlete_name}</strong>
                  <span style={{ background: chip.bg, color: chip.fg, borderRadius: 999,
                                 padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                    {chip.label}
                  </span>
                  {j.status === "live" && j.page_url && (
                    <a href={j.page_url} target="_blank" rel="noreferrer"
                       style={{ fontSize: 13 }}>View site</a>
                  )}
                  {j.status === "failed" && (
                    <button onClick={() => rerun(j)} disabled={busy === j.id}
                      style={{ padding: "3px 10px", borderRadius: 8, fontSize: 12,
                               border: "1px solid #d1d5db", cursor: "pointer" }}>
                      Re-run
                    </button>
                  )}
                  {j.status !== "building" && (
                    <button onClick={() => dismiss(j)} disabled={busy === j.id}
                      title={j.status === "queued" || j.status === "needs_input"
                        ? "Cancel this request" : "Remove from this list"}
                      style={{ padding: "3px 10px", borderRadius: 8, fontSize: 12,
                               border: "1px solid #d1d5db", cursor: "pointer",
                               marginLeft: "auto", color: "#6b7280" }}>
                      {j.status === "queued" || j.status === "needs_input" ? "Cancel" : "Dismiss"}
                    </button>
                  )}
                </div>
                {j.status === "failed" && j.error && (
                  <div style={{ fontSize: 12, color: "#b91c1c" }}>{j.error}</div>
                )}
                {j.status === "needs_input" && j.question && (
                  <div style={{ background: "#fffbeb", border: "1px solid #fde68a",
                                borderRadius: 8, padding: 10, display: "grid", gap: 8 }}>
                    <div style={{ fontSize: 13, color: "#78350f" }}>{j.question.text}</div>
                    {(j.question.options || []).length > 0 && (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {j.question.options.map((o) => (
                          <button key={o} disabled={busy === j.id}
                            onClick={() => sendAnswer(j, o)}
                            style={{ padding: "4px 12px", borderRadius: 8, fontSize: 13,
                                     border: "1px solid #d1d5db", cursor: "pointer" }}>
                            {o}
                          </button>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 8 }}>
                      <input style={{ ...FIELD, flex: 1 }} placeholder="Or type an answer…"
                        value={answers[j.id] || ""}
                        onChange={(e) => setAnswers({ ...answers, [j.id]: e.target.value })} />
                      <button disabled={busy === j.id}
                        onClick={() => sendAnswer(j, answers[j.id])}
                        style={{ padding: "6px 12px", borderRadius: 8, fontSize: 13,
                                 border: "1px solid #d1d5db", cursor: "pointer" }}>
                        Send
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
